/**
 * EquipmentAdminCard — card compacto de UNIDADE (equipment) usado no split
 * panel de Admin → Ativos.
 *
 * Diferente do EquipmentCard (operacional, mostra temperatura/porta), esse
 * é administrativo — foca em metadata: kind, slug, setpoint range, devices
 * vinculados, active/archived. Ações Editar/Deletar.
 */

import { Pencil, Snowflake, Trash2 } from "lucide-react";
import clsx from "clsx";

import type { Equipment } from "@/types";

import { Badge } from "./Badge";
import { Button } from "./Button";

const KIND_LABEL: Record<string, string> = {
  fridge:  "Geladeira",
  freezer: "Freezer",
  ultra:   "Ultra-low (-80 °C)",
  other:   "Outro",
};

interface Props {
  equipment: Equipment;
  /** Quantos devices ativos estão vinculados a esse ativo. */
  devicesCount: number;
  onEdit?: () => void;
  onDelete?: () => void;
}

export function EquipmentAdminCard({ equipment, devicesCount, onEdit, onDelete }: Props) {
  const meta = (equipment.metadata ?? {}) as Record<string, unknown>;
  const setpointMin = meta.setpoint_min;
  const setpointMax = meta.setpoint_max;
  const hasRange = setpointMin != null || setpointMax != null;
  const archived = !equipment.active;

  return (
    <div
      className={clsx(
        "flex flex-col gap-3 rounded-lg border p-4 transition-shadow",
        "border-surface-200 dark:border-slate-800",
        "bg-white dark:bg-slate-900 hover:shadow-card-hover",
        archived && "opacity-60",
      )}
    >
      {/* Header — nome + kind */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Snowflake className="h-4 w-4 flex-none text-brand-600 dark:text-brand-400" />
            <span
              className={clsx(
                "truncate text-sm font-semibold",
                archived
                  ? "text-slate-500 dark:text-slate-500 line-through"
                  : "text-slate-900 dark:text-slate-100",
              )}
            >
              {equipment.name}
            </span>
          </div>
          <div className="mt-1 flex items-center gap-1.5">
            <Badge tone="brand">{KIND_LABEL[equipment.kind ?? ""] ?? equipment.kind ?? "—"}</Badge>
          </div>
        </div>
        {archived && <Badge tone="neutral">Arquivada</Badge>}
      </div>

      {/* Metadata rápida */}
      <div className="grid grid-cols-2 gap-3 border-t border-surface-100 dark:border-slate-800 pt-3 text-xs">
        <div>
          <div className="text-slate-500 dark:text-slate-400">Slug</div>
          <code className="text-slate-700 dark:text-slate-300">{equipment.slug}</code>
        </div>
        <div>
          <div className="text-slate-500 dark:text-slate-400">Devices</div>
          <div className="tabular-nums text-slate-700 dark:text-slate-300">
            {devicesCount}
          </div>
        </div>
        {hasRange && (
          <div className="col-span-2">
            <div className="text-slate-500 dark:text-slate-400">Setpoint</div>
            <div className="tabular-nums text-slate-700 dark:text-slate-300">
              {String(setpointMin ?? "—")} a {String(setpointMax ?? "—")} °C
            </div>
          </div>
        )}
        {equipment.nominal_power_w && (
          <div className="col-span-2">
            <div className="text-slate-500 dark:text-slate-400">Potência nominal</div>
            <div className="tabular-nums text-slate-700 dark:text-slate-300">
              {equipment.nominal_power_w} W
              {equipment.nominal_voltage_v && ` @ ${equipment.nominal_voltage_v} V`}
              {equipment.phase && equipment.phase !== "mono" && ` · ${equipment.phase}`}
            </div>
          </div>
        )}
      </div>

      {/* Ações */}
      <div className="mt-auto flex items-center justify-end gap-1">
        {onEdit && (
          <Button
            size="sm"
            variant="ghost"
            icon={<Pencil className="h-3.5 w-3.5" />}
            onClick={onEdit}
          >
            Editar
          </Button>
        )}
        {onDelete && (
          <Button
            size="sm"
            variant="ghost"
            icon={<Trash2 className="h-3.5 w-3.5 text-red-500" />}
            onClick={onDelete}
          />
        )}
      </div>
    </div>
  );
}
